import Image from 'next/image';
import Link from 'next/link';
import { InstagramProductPhoto } from '@/components/instagram/instagram-product-photo';
import type { PublicInstagramProductPhoto } from '@/lib/api';
import type { CSSProperties, ReactNode } from 'react';
import {
  googleFontsUrl,
  type DesignTokens,
  type ProductRef,
  type Section,
  type StorefrontVersionDoc,
  type TileVariant,
} from '@sweetspot/shared';

/**
 * Public renderer for a storefront version (PRD §4.4). Presentational and
 * server-component friendly: tokens become CSS custom properties on
 * `.sf-root`, sections render from a registry, and product bindings resolve
 * against the SAME product list the legacy page already fetches. The editor
 * (later) renders this exact component, which is what makes preview = live.
 */

export interface RendererBusiness {
  id: string;
  name: string;
  slug: string | null;
  avatarUrl: string | null;
  coverImageUrl: string | null;
  /** Brand assets (Design tab → Brand): wide wordmark and square mark, both optional. */
  logoUrl?: string | null;
  logoMarkUrl?: string | null;
  bio: string | null;
  pinnedInstructions: string | null;
  locationNeighborhood: string | null;
  locationCity: string | null;
  locationRegion: string | null;
  avgRating: number | null;
  ratingCount: number;
  website?: string | null;
  handleInstagram?: string | null;
  /** API-computed entitlement result; false hides the Sweetspot footer link. */
  poweredBy?: boolean;
}

/** The public product shape from GET /storefront/business/:slug/products (Prisma row minus internal keys). */
export interface RendererProduct {
  id: string;
  title: string;
  description?: string | null;
  price: string | number;
  unit?: string | null;
  imageUrl?: string | null;
  instagramPhoto?: PublicInstagramProductPhoto | null;
  category?: string | null;
  createdAt?: string | Date;
}

/* ── tokens → css ─────────────────────────────────────────────────────── */

const RADIUS: Record<DesignTokens['radius'], string> = { none: '0px', sm: '4px', md: '10px', lg: '18px', full: '999px' };
const DENSITY: Record<DesignTokens['density'], string> = { compact: 'py-6', regular: 'py-10', airy: 'py-16' };
const MAX_WIDTH: Record<DesignTokens['maxWidth'], string> = { narrow: 'max-w-3xl', regular: 'max-w-4xl', wide: 'max-w-6xl' };
const CROP: Record<DesignTokens['imageCrop'], string> = {
  natural: 'aspect-[4/3]',
  square: 'aspect-square',
  arch: 'aspect-[3/4] rounded-t-[999px]',
};

function tokenStyle(t: DesignTokens): CSSProperties {
  return {
    ['--sf-bg' as string]: t.colorBg,
    ['--sf-surface' as string]: t.colorSurface,
    ['--sf-text' as string]: t.colorText,
    ['--sf-muted' as string]: t.colorMuted,
    ['--sf-primary' as string]: t.colorPrimary,
    ['--sf-on-primary' as string]: t.colorOnPrimary,
    ['--sf-accent' as string]: t.colorAccent,
    ['--sf-radius' as string]: RADIUS[t.radius],
    ['--sf-font-heading' as string]: `"${t.fontHeading}", ui-serif, Georgia, ui-sans-serif, system-ui, sans-serif`,
    ['--sf-font-body' as string]: `"${t.fontBody}", ui-sans-serif, system-ui, sans-serif`,
    ['--sf-heading-weight' as string]: t.headingWeight,
    ['--sf-heading-case' as string]: t.headingCase,
    background: 'var(--sf-bg)',
    color: 'var(--sf-text)',
    fontFamily: 'var(--sf-font-body)',
  };
}

const heading: CSSProperties = {
  fontFamily: 'var(--sf-font-heading)',
  fontWeight: 'var(--sf-heading-weight)' as unknown as number,
  textTransform: 'var(--sf-heading-case)' as CSSProperties['textTransform'],
};

function buttonClass(t: DesignTokens): string {
  const shape = t.buttonShape === 'pill' ? 'rounded-full' : t.buttonShape === 'square' ? 'rounded-none' : 'rounded-[var(--sf-radius)]';
  const style =
    t.buttonStyle === 'outline'
      ? 'border-2 border-[var(--sf-primary)] text-[var(--sf-primary)] bg-transparent'
      : t.buttonStyle === 'soft'
        ? 'bg-[var(--sf-primary)]/15 text-[var(--sf-primary)]'
        : 'bg-[var(--sf-primary)] text-[var(--sf-on-primary)]';
  return `inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold transition-opacity hover:opacity-90 ${shape} ${style}`;
}

/* ── data binding ─────────────────────────────────────────────────────── */

export function resolveProducts(ref: ProductRef, products: RendererProduct[]): RendererProduct[] {
  if (ref.mode === 'manual') {
    const byId = new Map(products.map((p) => [p.id, p]));
    return ref.ids.map((id) => byId.get(id)).filter((p): p is RendererProduct => Boolean(p));
  }
  // The public products endpoint already returns newest first.
  const pool = ref.rule === 'newest'
    ? products
    : products.filter((p) => (p.category ?? '').toLowerCase() === ref.rule.slice('category:'.length).toLowerCase());
  return pool.slice(0, ref.limit);
}

function money(v: string | number): string {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(v);
}

/* ── building blocks ──────────────────────────────────────────────────── */

function Container({ t, children, className = '' }: { t: DesignTokens; children: ReactNode; className?: string }) {
  return <div className={`mx-auto w-full ${MAX_WIDTH[t.maxWidth]} px-4 ${className}`}>{children}</div>;
}

function SectionHeading({ text }: { text?: string }) {
  if (!text) return null;
  return <h2 className="mb-5 text-2xl" style={heading}>{text}</h2>;
}

function ProductTile({ p, slug, t, variant }: { p: RendererProduct; slug: string; t: DesignTokens; variant: TileVariant }) {
  const href = `/s/${slug}/p/${p.id}`;
  const crop = `${CROP[t.imageCrop]} ${variant === 'card' ? '' : 'rounded-[var(--sf-radius)]'}`;
  const img = (
    <InstagramProductPhoto photo={p.instagramPhoto} title={p.title} imageHref={href}
      className={crop} imageClassName={`h-full w-full object-cover ${crop}`}>
      <div className={`relative w-full overflow-hidden ${crop}`} style={{ background: 'var(--sf-surface)' }}>
        {p.imageUrl ? (
          <Image src={p.imageUrl} alt={p.title} fill sizes="(max-width: 768px) 50vw, 25vw" className="object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-4xl" style={{ color: 'var(--sf-muted)' }}>🥐</div>
        )}
      </div>
    </InstagramProductPhoto>
  );
  if (variant === 'minimal') {
    return (
      <div className="group block">
        {img}
        <Link href={href} prefetch={false} className="mt-2 flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium group-hover:underline">{p.title}</span>
          <span className="shrink-0 text-sm" style={{ color: 'var(--sf-muted)' }}>{money(p.price)}</span>
        </Link>
      </div>
    );
  }
  return (
    <div className="group block overflow-hidden rounded-[var(--sf-radius)] border" style={{ background: 'var(--sf-surface)', borderColor: 'color-mix(in srgb, var(--sf-text) 12%, transparent)' }}>
      {img}
      <Link href={href} prefetch={false} className="block p-3">
        <p className="truncate text-sm font-medium group-hover:underline">{p.title}</p>
        <p className="mt-0.5 text-sm font-semibold" style={{ color: 'var(--sf-primary)' }}>
          {money(p.price)}{p.unit ? <span className="font-normal" style={{ color: 'var(--sf-muted)' }}> / {p.unit}</span> : null}
        </p>
      </Link>
    </div>
  );
}

function ProductGrid({ items, slug, t, variant, cols }: { items: RendererProduct[]; slug: string; t: DesignTokens; variant: TileVariant; cols: string }) {
  if (!items.length) return <p className="text-sm" style={{ color: 'var(--sf-muted)' }}>No products yet.</p>;
  return (
    <div className={`grid gap-4 ${cols}`}>
      {items.map((p) => <ProductTile key={p.id} p={p} slug={slug} t={t} variant={variant} />)}
    </div>
  );
}

/* ── sections ─────────────────────────────────────────────────────────── */

function locationLine(b: RendererBusiness): string | null {
  const n = b.locationNeighborhood?.trim();
  const c = (b.locationCity || b.locationRegion || '').trim();
  if (n && c && n.toLowerCase() === c.toLowerCase()) return c;
  const parts = [n || null, c || null].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

/** Resolve the header's `logo` setting against what the business actually uploaded. */
function headerLogoMode(s: Extract<Section, { type: 'header' }>, b: RendererBusiness): 'wordmark' | 'mark' | 'text' {
  const want = s.logo ?? 'auto';
  if (want === 'wordmark' || want === 'auto') {
    if (b.logoUrl) return 'wordmark';
    if (want === 'auto' && b.logoMarkUrl) return 'mark';
    return want === 'auto' ? 'text' : 'text';
  }
  if (want === 'mark') return b.logoMarkUrl ? 'mark' : 'text';
  return 'text';
}

function Header({ s, b, t }: { s: Extract<Section, { type: 'header' }>; b: RendererBusiness; t: DesignTokens }) {
  const centered = s.variant === 'centered';
  const loc = locationLine(b);
  const mode = headerLogoMode(s, b);
  // The badge at the left (or above): the mark when there is one, else the avatar, else an initial.
  const badge = b.logoMarkUrl ? (
    <div className="relative h-14 w-14 shrink-0">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={b.logoMarkUrl} alt="" className="h-14 w-14 object-contain" />
    </div>
  ) : b.avatarUrl ? (
    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full border-2" style={{ borderColor: 'var(--sf-surface)' }}>
      <Image src={b.avatarUrl} alt="" fill className="object-cover" />
    </div>
  ) : (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-xl font-bold" style={{ background: 'var(--sf-primary)', color: 'var(--sf-on-primary)' }}>
      {b.name.charAt(0).toUpperCase()}
    </div>
  );
  return (
    <header className="py-6">
      <Container t={t} className={`flex gap-4 ${centered ? 'flex-col items-center text-center' : 'items-center'}`}>
        {mode === 'wordmark' && !centered ? (b.logoMarkUrl ? badge : null) : badge}
        <div className="min-w-0">
          {mode === 'wordmark' && b.logoUrl ? (
            <h1 className="leading-none">
              {/* A wordmark IS the name — keep the text for assistive tech and search. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={b.logoUrl} alt={b.name} className={`w-auto max-w-[280px] object-contain object-left ${centered ? 'mx-auto h-14 object-center' : 'h-10'}`} />
              <span className="sr-only">{b.name}</span>
            </h1>
          ) : (
            <h1 className="text-2xl leading-tight" style={heading}>{b.name}</h1>
          )}
          <p className="text-sm" style={{ color: 'var(--sf-muted)', fontStyle: t.accentItalic ? 'italic' : undefined }}>
            {[s.tagline, loc].filter(Boolean).join(' · ')}
            {s.showRating && b.ratingCount > 0 ? ` · ★ ${(b.avgRating ?? 0).toFixed(1)} (${b.ratingCount})` : ''}
          </p>
        </div>
      </Container>
    </header>
  );
}

function Announcement({ s, t }: { s: Extract<Section, { type: 'announcement' }>; t: DesignTokens }) {
  const style: CSSProperties =
    s.variant === 'solid'
      ? { background: 'var(--sf-primary)', color: 'var(--sf-on-primary)' }
      : s.variant === 'outline'
        ? {
            borderColor: 'color-mix(in srgb, var(--sf-primary) 35%, transparent)',
            color: 'var(--sf-text)',
          }
        : {
            background: 'color-mix(in srgb, var(--sf-primary) 12%, var(--sf-bg))',
            color: 'var(--sf-text)',
          };
  return (
    <aside
      aria-label="Announcement"
      className={s.variant === 'outline' ? 'border-y py-2.5 text-sm' : 'py-2.5 text-sm'}
      style={style}
    >
      <Container t={t} className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
        <span>{s.text}</span>
        {s.linkLabel && s.linkUrl && (
          <a href={s.linkUrl} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-4">
            {s.linkLabel}
          </a>
        )}
      </Container>
    </aside>
  );
}

function Hero({ s, b, t }: { s: Extract<Section, { type: 'hero' }>; b: RendererBusiness; t: DesignTokens }) {
  const image = s.imageUrl ?? b.coverImageUrl ?? null;
  const cta = s.ctaLabel ? <a href={`#sf-${s.ctaAnchor}`} className={buttonClass(t)}>{s.ctaLabel}</a> : null;
  const text = (
    <div className={s.variant === 'minimal' ? 'max-w-2xl' : 'max-w-xl'}>
      {s.headline && <h2 className="text-4xl leading-tight md:text-5xl" style={heading}>{s.headline}</h2>}
      {s.subheadline && (
        <p className="mt-3 text-lg" style={{ color: s.variant === 'cover' && image ? 'inherit' : 'var(--sf-muted)', fontStyle: t.accentItalic ? 'italic' : undefined }}>
          {s.subheadline}
        </p>
      )}
      {cta && <div className="mt-6">{cta}</div>}
    </div>
  );
  if (s.variant === 'cover' && image) {
    return (
      <section className="relative min-h-[320px] md:min-h-[420px]">
        <Image src={image} alt="" fill priority sizes="100vw" className="object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <Container t={t} className="relative flex min-h-[320px] items-center py-12 text-white md:min-h-[420px]">{text}</Container>
      </section>
    );
  }
  if (s.variant === 'split') {
    return (
      <section className={DENSITY[t.density]}>
        <Container t={t} className="grid items-center gap-8 md:grid-cols-2">
          {text}
          <div className={`relative w-full overflow-hidden rounded-[var(--sf-radius)] ${CROP[t.imageCrop]}`} style={{ background: 'var(--sf-surface)' }}>
            {image && <Image src={image} alt="" fill sizes="50vw" className="object-cover" />}
          </div>
        </Container>
      </section>
    );
  }
  const gradient = s.variant === 'gradient' || (s.variant === 'cover' && !image);
  return (
    <section
      className={DENSITY[t.density]}
      style={gradient ? { background: 'linear-gradient(135deg, var(--sf-primary) 0%, var(--sf-accent) 100%)', color: 'var(--sf-on-primary)' } : undefined}
    >
      <Container t={t} className={s.variant === 'minimal' ? 'text-center' : ''}>{text}</Container>
    </section>
  );
}

function Story({ s, t }: { s: Extract<Section, { type: 'story' }>; t: DesignTokens }) {
  return (
    <section className={DENSITY[t.density]}>
      <Container t={t} className={s.variant === 'text-image' && s.imageUrl ? 'grid gap-8 md:grid-cols-2' : ''}>
        <div>
          <SectionHeading text={s.heading} />
          <p className="whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--sf-muted)' }}>{s.body}</p>
        </div>
        {s.variant === 'text-image' && s.imageUrl && (
          <div className={`relative w-full overflow-hidden rounded-[var(--sf-radius)] ${CROP[t.imageCrop]}`}>
            <Image src={s.imageUrl} alt="" fill sizes="50vw" className="object-cover" />
          </div>
        )}
      </Container>
    </section>
  );
}

function Featured({ s, products, slug, t }: { s: Extract<Section, { type: 'featured-products' }>; products: RendererProduct[]; slug: string; t: DesignTokens }) {
  const items = resolveProducts(s.products, products);
  const tile = s.tile ?? t.tileVariant;
  const spotlight = s.variant === 'spotlight' ? items.slice(0, 1) : null;
  return (
    <section className={DENSITY[t.density]}>
      <Container t={t}>
        <SectionHeading text={s.heading} />
        {s.variant === 'row' ? (
          <div className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-2">
            {items.map((p) => <div key={p.id} className="w-44 shrink-0 md:w-56"><ProductTile p={p} slug={slug} t={t} variant={tile} /></div>)}
          </div>
        ) : spotlight ? (
          <div className="grid gap-6 md:grid-cols-[3fr_2fr]">
            {spotlight.map((p) => <ProductTile key={p.id} p={p} slug={slug} t={t} variant={tile} />)}
            <ProductGrid items={items.slice(1, 5)} slug={slug} t={t} variant={tile} cols="grid-cols-2" />
          </div>
        ) : (
          <ProductGrid items={items} slug={slug} t={t} variant={tile} cols="grid-cols-2 md:grid-cols-4" />
        )}
      </Container>
    </section>
  );
}

function Seasonal({ s, products, slug, t }: { s: Extract<Section, { type: 'seasonal' }>; products: RendererProduct[]; slug: string; t: DesignTokens }) {
  const items = resolveProducts(s.products, products);
  const copy = (
    <div className="max-w-xl">
      {s.eyebrow && (
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--sf-primary)' }}>
          {s.eyebrow}
        </p>
      )}
      <h2 className="text-3xl leading-tight md:text-4xl" style={heading}>{s.heading}</h2>
      {s.body && <p className="mt-3 whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--sf-muted)' }}>{s.body}</p>}
      {s.ctaLabel && <a href="#sf-products" className={`${buttonClass(t)} mt-5`}>{s.ctaLabel}</a>}
    </div>
  );

  return (
    <section
      className={DENSITY[t.density]}
      style={{ background: 'color-mix(in srgb, var(--sf-accent) 14%, var(--sf-bg))' }}
    >
      <Container t={t} className={s.variant === 'split' ? 'grid items-center gap-8 md:grid-cols-[2fr_3fr]' : ''}>
        {copy}
        <div className={s.variant === 'banner' ? 'mt-7' : ''}>
          <ProductGrid
            items={items.slice(0, s.variant === 'split' ? 2 : 4)}
            slug={slug}
            t={t}
            variant={t.tileVariant}
            cols={s.variant === 'split' ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}
          />
        </div>
      </Container>
    </section>
  );
}

function Catalog({ s, products, slug, t }: { s: Extract<Section, { type: 'catalog' }>; products: RendererProduct[]; slug: string; t: DesignTokens }) {
  const tile = s.tile ?? t.tileVariant;
  const categories = s.showCategoryFilter
    ? Array.from(new Set(products.map((p) => p.category?.trim()).filter((c): c is string => Boolean(c))))
    : [];
  return (
    <section id="sf-products" className={DENSITY[t.density]}>
      <Container t={t}>
        <SectionHeading text={s.heading} />
        {categories.length > 1 && (
          <div className="mb-4 flex flex-wrap gap-2 text-xs" style={{ color: 'var(--sf-muted)' }}>
            {categories.map((c) => <span key={c} className="rounded-full border px-2.5 py-1">{c}</span>)}
          </div>
        )}
        {s.variant === 'list' ? (
          <ul className="divide-y" style={{ borderColor: 'color-mix(in srgb, var(--sf-text) 12%, transparent)' }}>
            {products.map((p) => (
              <li key={p.id}>
                <Link href={`/s/${slug}/p/${p.id}`} className="flex items-center justify-between gap-4 py-3 hover:underline">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{p.title}</p>
                    {p.description && <p className="truncate text-sm" style={{ color: 'var(--sf-muted)' }}>{p.description}</p>}
                  </div>
                  <span className="shrink-0 font-semibold" style={{ color: 'var(--sf-primary)' }}>{money(p.price)}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <ProductGrid items={products} slug={slug} t={t} variant={tile} cols="grid-cols-2 md:grid-cols-3" />
        )}
      </Container>
    </section>
  );
}

function HoursLocation({ s, b, t }: { s: Extract<Section, { type: 'hours-location' }>; b: RendererBusiness; t: DesignTokens }) {
  const address = s.addressText ?? locationLine(b);
  return (
    <section id="sf-hours" className={DENSITY[t.density]} style={{ background: 'var(--sf-surface)' }}>
      <Container t={t}>
        <SectionHeading text={s.heading} />
        <div className={s.variant === 'columns' ? 'grid gap-6 md:grid-cols-2' : 'space-y-4'}>
          {s.hoursText && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--sf-muted)' }}>Hours</p>
              <p className="whitespace-pre-wrap">{s.hoursText}</p>
            </div>
          )}
          {address && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--sf-muted)' }}>Pickup</p>
              <p>{address}</p>
            </div>
          )}
        </div>
        {s.note && <p className="mt-4 text-sm" style={{ color: 'var(--sf-muted)' }}>{s.note}</p>}
        {b.pinnedInstructions && <p className="mt-4 whitespace-pre-wrap text-sm" style={{ color: 'var(--sf-muted)' }}>{b.pinnedInstructions}</p>}
      </Container>
    </section>
  );
}

function StickyCta({ s, t }: { s: Extract<Section, { type: 'sticky-cta' }>; t: DesignTokens }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t p-3 md:hidden" style={{ background: 'var(--sf-surface)', borderColor: 'color-mix(in srgb, var(--sf-text) 12%, transparent)' }}>
      <a href={`#sf-${s.ctaAnchor}`} className={`${buttonClass(t)} w-full`}>{s.label}</a>
    </div>
  );
}

function Footer({ s, b, t }: { s: Extract<Section, { type: 'footer' }>; b: RendererBusiness; t: DesignTokens }) {
  const links: { label: string; href: string }[] = [];
  if (s.showWebsite && b.website) links.push({ label: b.website.replace(/^https?:\/\//, ''), href: b.website });
  if (s.showInstagram && b.handleInstagram) links.push({ label: `@${b.handleInstagram.replace(/^@/, '')}`, href: `https://instagram.com/${b.handleInstagram.replace(/^@/, '')}` });
  return (
    <footer className="py-10 pb-24 text-sm md:pb-10" style={{ color: 'var(--sf-muted)' }}>
      <Container t={t} className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2">
          {b.logoMarkUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={b.logoMarkUrl} alt="" className="h-6 w-6 object-contain opacity-80" />
          )}
          © {new Date().getFullYear()} {b.name}
        </span>
        <span className="flex gap-4">
          {links.map((l) => <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" className="hover:underline">{l.label}</a>)}
          {b.poweredBy !== false && <Link href="/marketplace" className="hover:underline">Sweetspot</Link>}
        </span>
        {s.note && <p className="w-full">{s.note}</p>}
      </Container>
    </footer>
  );
}

/* ── root ─────────────────────────────────────────────────────────────── */

/**
 * Reuse the builder's announcement rendering on the ordering-site shell.
 * The caller owns the surrounding CSS-variable aliases so the announcement
 * inherits that page's active theme instead of replacing it with a full
 * storefront document theme.
 */
export function StorefrontPromotions({
  tokens,
  sections,
}: {
  tokens: DesignTokens;
  sections: Extract<Section, { type: 'announcement' }>[];
}) {
  return (
    <>
      {sections
        .filter((section) => !section.hidden)
        .map((section) => <Announcement key={section.id} s={section} t={tokens} />)}
    </>
  );
}

export function StorefrontRenderer({ doc, business, products, slug }: { doc: StorefrontVersionDoc; business: RendererBusiness; products: RendererProduct[]; slug: string }) {
  const t = doc.tokens;
  // The font tokens are Google Fonts families; without this the browser silently
  // falls back and every template looks the same. A plain <style>@import> on
  // purpose: a <link precedence> is a React 19 "suspensey" stylesheet, and in
  // the dashboard modal the suspension re-mounted the dialog (measured: the
  // Design tab snapped back to Listing every time a preview rendered).
  const fontsHref = googleFontsUrl([t.fontHeading, t.fontBody], { italic: t.accentItalic });
  return (
    <div className="sf-root min-h-screen" style={tokenStyle(t)}>
      {fontsHref && <style>{`@import url("${fontsHref}");`}</style>}
      {doc.sections.filter((s) => !s.hidden).map((s) => {
        switch (s.type) {
          case 'header': return <Header key={s.id} s={s} b={business} t={t} />;
          case 'announcement': return <Announcement key={s.id} s={s} t={t} />;
          case 'hero': return <Hero key={s.id} s={s} b={business} t={t} />;
          case 'story': return <Story key={s.id} s={s} t={t} />;
          case 'featured-products': return <Featured key={s.id} s={s} products={products} slug={slug} t={t} />;
          case 'seasonal': return <Seasonal key={s.id} s={s} products={products} slug={slug} t={t} />;
          case 'catalog': return <Catalog key={s.id} s={s} products={products} slug={slug} t={t} />;
          case 'hours-location': return <HoursLocation key={s.id} s={s} b={business} t={t} />;
          case 'sticky-cta': return <StickyCta key={s.id} s={s} t={t} />;
          case 'footer': return <Footer key={s.id} s={s} b={business} t={t} />;
          default: return null; // unknown type — the schema already rejects these, belt and braces
        }
      })}
    </div>
  );
}
